/* =====================================================================================
   Travel.

   REQUEST, APPROVE, HAND TO A BOOKING CHANNEL. This screen never books a seat and never
   pays for one, for the same reason Expenses never pays a claim: booking and payment live
   where the commercial controls already exist (a booking platform, an agency, a card with
   a limit), and duplicating them here would mean holding card details and fare rules that
   are somebody else's job. Forum records what was asked for, who approved it, which
   channel it was handed to, and what came back.

   APPROVAL IS EXPENSES' ROUTING, DELIBERATELY. A trip request goes to whoever that
   member's expense claims go to: the line manager from Crew, the owner for the owner and
   the board, an explicit override winning, the owner as fallback. One routing rule in one
   place (ledger.expense_approver_for), so when Crew changes, both modules follow, and
   nobody learns two different answers to "who signs my things off".

   FULFILMENT IS TENANT CONFIGURATION. The booking channels on an approved request come
   from the tenant file, not from this module: a TravelPerk deep link for a tenant that has
   TravelPerk, a prefilled email for a tenant with an agency, "book it yourself" for one
   with neither. No agency or platform is named in shared code. TravelPerk is the one
   first-class provider (lib/travelperk.ts): a handoff out plus a scaffolded sync back,
   because its public API deliberately has no booking-creation endpoint.

   THE TAB WARNING. The request screen warns when it is already open in another tab, keyed
   per module: two half-written copies of the same trip in two tabs is how a request gets
   submitted twice, and the person with two tabs open knows which to close.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import { Boundary } from '../components/Boundary';
import { Attachments } from '../components/Attachments';
import { useDialog } from '../lib/ui';
import { useTabPresence } from '../lib/tabPresence';
import {
  travelPerkAppUrl, describeTravelPerkConnection,
} from '../lib/travelperk';
import type { TravelBookingChannel } from '../tenants/types';
import './travel.css';

interface Request {
  id: string; reference: string | null; member_id: string; traveller: string;
  purpose: string; origin: string; destination: string;
  depart_on: string; return_on: string | null; mode_preferences: string | null;
  estimated_cost: number | null; currency: string | null; cost_centre: string | null;
  notes: string | null; status: string;
  submitted_at: string | null; approver_id: string | null; approver_source: string | null;
  decided_at: string | null; decided_as: string | null; decision_note: string | null;
  self_approved: boolean;
  booking_channel: string | null; booking_channel_label: string | null;
  booking_requested_at: string | null; booked_at: string | null;
  completed_at: string | null; cancelled_at: string | null; cancel_reason: string | null;
}

/* ledger.v_my_travel_approvals: the requests I may decide, with who they were routed to. */
interface Approval {
  id: string; reference: string | null; traveller: string; purpose: string;
  origin: string; destination: string; depart_on: string; return_on: string | null;
  mode_preferences: string | null; estimated_cost: number | null; currency: string | null;
  cost_centre: string | null; notes: string | null; submitted_at: string | null;
  requester: string; member_id: string; is_own: boolean;
  approver_id: string | null; approver_source: string | null;
  approver_name: string | null; is_approver: boolean;
}

const ROUTE_LABEL: Record<string, string> = {
  line_manager: 'as line manager', owner: 'as owner', override: 'by assignment',
  fallback: 'as owner, because no line manager is recorded in Crew',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'draft', submitted: 'submitted', approved: 'approved', declined: 'declined',
  booking_requested: 'awaiting confirmation', booked: 'booked',
  completed: 'completed', cancelled: 'cancelled',
};

const BASE = TENANT.brand.baseCurrency ?? 'GBP';
const money = (n: number, c?: string | null) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: c || BASE }).format(n);
const day = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB',
  { day: 'numeric', month: 'short', year: 'numeric' });

/* The itinerary-request summary, one shape for every channel: it is the clipboard text,
   the email body, and what gets pasted into a booking platform. The reference is the first
   line on purpose: it is what a webhook or a human matches the confirmation back by. */
function tripSummary(r: Request): string {
  return [
    `Travel booking request ${r.reference ?? '(draft)'}`,
    `Traveller: ${r.traveller}`,
    `Purpose: ${r.purpose}`,
    `From: ${r.origin}`,
    `To: ${r.destination}`,
    `Outbound: ${day(r.depart_on)}`,
    r.return_on ? `Return: ${day(r.return_on)}` : 'One way',
    r.mode_preferences ? `Preferences: ${r.mode_preferences}` : null,
    r.estimated_cost != null
      ? `Approved estimate: ${r.currency ?? BASE} ${Number(r.estimated_cost).toFixed(2)}` : null,
    r.cost_centre ? `Cost centre: ${r.cost_centre}` : null,
    r.notes ? `Notes: ${r.notes}` : null,
    '',
    `Approved in ${TENANT.brand.productName}. Please reply with the itinerary and `
      + 'booking confirmation, quoting the reference above.',
  ].filter((x): x is string => x !== null).join('\n');
}

export function TravelView() {
  const [requests, setRequests] = useState<Request[] | null>(null);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [approverNames, setApproverNames] = useState<Record<string, string>>({});
  const [me, setMe] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Request> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const live = BACKEND === 'supabase';

  const load = useCallback(async () => {
    if (!live) { setRequests([]); return; }
    const { supabase } = await import('../lib/supabase');
    const app = supabase().schema('app');
    const led = supabase().schema('ledger');
    const [{ data: mid }, { data: r }, { data: a }] = await Promise.all([
      app.rpc('current_member_id'),
      led.from('travel_requests').select('*').order('created_at', { ascending: false }),
      led.from('v_my_travel_approvals').select('*').order('submitted_at'),
    ]);
    setMe(mid as string | null);
    setRequests((r ?? []) as unknown as Request[]);
    setApprovals((a ?? []) as unknown as Approval[]);
    const ids = Array.from(new Set(((r ?? []) as Request[])
      .map((x) => x.approver_id).filter(Boolean))) as string[];
    if (ids.length) {
      const { data: ms } = await app.from('members')
        .select('id, full_name, display_name').in('id', ids);
      setApproverNames(Object.fromEntries(
        ((ms ?? []) as { id: string; full_name: string; display_name: string | null }[])
          .map((m) => [m.id, m.display_name || m.full_name])));
    }
  }, [live]);
  useEffect(() => { load(); }, [load]);

  async function call(fn: string, args: Record<string, unknown>, ok: string) {
    setBusy(true); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('ledger').rpc(fn, args);
      if (error) throw new Error(error.message);
      setMsg(ok); await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  async function copy(text: string, what: string) {
    try { await navigator.clipboard.writeText(text); setMsg(`${what} copied.`); }
    catch { setMsg('The browser refused the clipboard. Select and copy the text instead.'); }
  }

  /* Handing an approved request to a channel: record the handoff, then let the channel's
     own control (mailto, deep link) do what it visibly says. A direct channel books
     nothing and waits for nothing, so it records the handoff and the booking in one go. */
  function handOff(r: Request, ch: TravelBookingChannel) {
    void call('request_travel_booking',
      { p_request: r.id, p_channel: ch.key, p_channel_label: ch.label },
      `Sent to ${ch.label}. Attach the confirmation when it arrives, then mark it booked.`);
  }
  async function bookedDirect(r: Request, ch: TravelBookingChannel) {
    setBusy(true); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const led = supabase().schema('ledger');
      const h = await led.rpc('request_travel_booking',
        { p_request: r.id, p_channel: ch.key, p_channel_label: ch.label });
      if (h.error) throw new Error(h.error.message);
      const b = await led.rpc('mark_travel_booked', { p_request: r.id });
      if (b.error) throw new Error(b.error.message);
      setMsg('Recorded as booked. Attach the confirmation so the trip carries its evidence.');
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  /* Export to expenses: a structured line the claim is built from, not a claim written for
     them. The receipts and the true amounts exist only in the traveller's hands, and the
     claim must be theirs; Forum exports, and nothing here pays or files on their behalf. */
  function copyForExpenses(r: Request) {
    void copy([
      `${r.depart_on} | travel | ${r.purpose}: ${r.origin} to ${r.destination}`
        + ` (${r.reference ?? 'travel request'})`,
      r.estimated_cost != null
        ? `Approved estimate ${r.currency ?? BASE} ${Number(r.estimated_cost).toFixed(2)}; `
          + 'claim the actual amounts, with receipts.'
        : 'Claim the actual amounts, with receipts.',
      r.cost_centre ? `Cost centre: ${r.cost_centre}` : null,
    ].filter(Boolean).join('\n'), 'Expense summary');
  }

  if (!requests) return <p className="muted">Loading</p>;

  const mine = requests.filter((r) => r.member_id === me);
  const channels = TENANT.travel.bookingChannels;
  const perk = TENANT.travel.perk;

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Travel</h1>
        <p className="page-sub">
          Request, approve, and hand to a booking channel. {TENANT.brand.productName} never
          books and never pays: fulfilment happens wherever this company has decided it does,
          and the confirmation comes back to the request.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {!live && (
        <p className="module-empty">
          This demonstration build does not store travel requests. On a live tenant this page
          holds each trip from request through approval, booking channel handoff and
          confirmation to the expense claim it ends in.
        </p>
      )}

      {approvals.length > 0 && (
        <section className="card">
          <h2>Awaiting your decision</h2>
          <p className="muted trv-route-note">
            A request comes to its requester's line manager, from Crew; the owner's and the
            board's come to the owner, exactly as expense claims route. Approve it or decline
            it with the reason.
          </p>
          {approvals.map((a) => (
            <div key={a.id} className="trv-req trv-approval">
              <div className="trv-req-head">
                <strong>{a.reference ?? 'request'}</strong>
                <span>{a.requester}</span>
                <span className="muted">
                  {a.origin} to {a.destination}, {day(a.depart_on)}
                  {a.return_on && <> to {day(a.return_on)}</>}
                </span>
                {a.estimated_cost != null && (
                  <span className="trv-cost">{money(Number(a.estimated_cost), a.currency)}</span>
                )}
              </div>
              <div className="trv-req-body">
                <p className="trv-purpose">{a.purpose}</p>
                <dl className="trv-facts">
                  <div><dt>Traveller</dt><dd>{a.traveller}</dd></div>
                  {a.mode_preferences && <div><dt>Preferences</dt><dd>{a.mode_preferences}</dd></div>}
                  {a.cost_centre && <div><dt>Cost centre</dt><dd>{a.cost_centre}</dd></div>}
                  {a.notes && <div><dt>Notes</dt><dd>{a.notes}</dd></div>}
                </dl>
                <p className="trv-route muted">
                  {a.is_approver
                    ? `With you ${ROUTE_LABEL[a.approver_source ?? ''] ?? ''}`
                    : `With ${a.approver_name ?? 'nobody'}; deciding it here is recorded as a finance override`}
                  {a.is_own && ' · your own request, recorded as self-approved'}
                </p>
                <div className="trv-actions">
                  <button className="btn primary" disabled={busy}
                          onClick={() => call('decide_travel_request',
                                              { p_request: a.id, p_approve: true }, 'Approved.')}>
                    Approve
                  </button>
                  <button className="btn" disabled={busy}
                          onClick={() => {
                            const why = window.prompt('Why is it declined? The requester sees '
                                                      + 'this and can amend and resubmit.');
                            if (why === null) return;
                            call('decide_travel_request',
                                 { p_request: a.id, p_approve: false, p_note: why.trim() },
                                 'Declined; the requester has been told.');
                          }}>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="card">
        <div className="trv-head">
          <h2>Your trips</h2>
          <button className="btn primary" disabled={busy}
                  onClick={() => setEditing({})}>
            New request
          </button>
        </div>

        {live && mine.length === 0 && <p className="muted">No trips yet.</p>}

        {mine.map((r) => {
          const isOpen = open === r.id;
          return (
            <div key={r.id} className="trv-req">
              <button className="trv-req-head trv-clickable" onClick={() => setOpen(isOpen ? null : r.id)}>
                <span className={`trv-status st-${r.status}`}>{STATUS_LABEL[r.status] ?? r.status}</span>
                {r.self_approved && <span className="trv-selftag">self-approved</span>}
                <strong>{r.reference ?? 'Draft'}</strong>
                <span className="muted">
                  {r.origin} to {r.destination}, {day(r.depart_on)}
                  {r.return_on && <> to {day(r.return_on)}</>}
                </span>
                {r.estimated_cost != null && (
                  <span className="trv-cost">{money(Number(r.estimated_cost), r.currency)}</span>
                )}
                <span className="trv-open">{isOpen ? 'Close' : 'Open'}</span>
              </button>

              {r.status === 'submitted' && r.approver_id && (
                <p className="trv-with muted">
                  With {r.approver_id === me ? 'you' : (approverNames[r.approver_id] ?? 'your approver')} for
                  approval {ROUTE_LABEL[r.approver_source ?? ''] ?? ''}.
                </p>
              )}
              {r.status === 'declined' && (
                <p className="trv-declined"><strong>Declined.</strong> {r.decision_note
                  ?? ''} Amend it and submit again if the trip still needs to happen.</p>
              )}

              {isOpen && (
                <div className="trv-req-body">
                  <p className="trv-purpose">{r.purpose}</p>
                  <dl className="trv-facts">
                    <div><dt>Traveller</dt><dd>{r.traveller}</dd></div>
                    {r.mode_preferences && <div><dt>Preferences</dt><dd>{r.mode_preferences}</dd></div>}
                    {r.cost_centre && <div><dt>Cost centre</dt><dd>{r.cost_centre}</dd></div>}
                    {r.notes && <div><dt>Notes</dt><dd>{r.notes}</dd></div>}
                    {r.booking_channel_label && (
                      <div><dt>Booking channel</dt><dd>{r.booking_channel_label}</dd></div>
                    )}
                  </dl>

                  {(r.status === 'draft' || r.status === 'declined') && (
                    <div className="trv-actions">
                      <button className="btn" disabled={busy} onClick={() => setEditing(r)}>Edit</button>
                      <button className="btn primary" disabled={busy}
                              onClick={() => call('submit_travel_request', { p_request: r.id },
                                                  'Submitted for approval.')}>
                        Submit for approval
                      </button>
                    </div>
                  )}

                  {r.status === 'approved' && (
                    <BookingChannels r={r} channels={channels} busy={busy}
                                     onHandOff={handOff} onBookedDirect={bookedDirect}
                                     onCopy={(text) => copy(text, 'Trip summary')} />
                  )}

                  {r.status === 'booking_requested' && (
                    <div className="trv-booking">
                      <p>
                        Sent to <strong>{r.booking_channel_label}</strong>
                        {r.booking_requested_at && <> on {new Date(r.booking_requested_at)
                          .toLocaleDateString('en-GB')}</>}, awaiting the confirmation.
                        Attach it below, then mark the trip booked.
                      </p>
                      <div className="trv-actions">
                        <button className="btn primary" disabled={busy}
                                onClick={() => call('mark_travel_booked', { p_request: r.id },
                                                    'Booked.')}>
                          Mark as booked
                        </button>
                        <button className="btn" disabled={busy}
                                onClick={() => copy(tripSummary(r), 'Trip summary')}>
                          Copy the request again
                        </button>
                      </div>
                    </div>
                  )}

                  {r.status === 'booked' && (
                    <div className="trv-actions">
                      <button className="btn primary" disabled={busy}
                              onClick={() => call('complete_travel_request', { p_request: r.id },
                                                  'Completed. Build the expense claim from it below.')}>
                        Trip completed
                      </button>
                    </div>
                  )}

                  {r.status === 'completed' && (
                    <div className="trv-export">
                      <p>
                        Claim it back through Expenses: copy the structured line, start this
                        month's claim, and attach the receipts there.
                        {' '}{TENANT.brand.productName} exports; it never pays.
                      </p>
                      <div className="trv-actions">
                        <button className="btn" onClick={() => copyForExpenses(r)}>
                          Copy for an expense claim
                        </button>
                        <Link className="btn" to="/expenses">Open Expenses</Link>
                      </div>
                    </div>
                  )}

                  {['approved', 'booking_requested', 'booked', 'completed'].includes(r.status) && (
                    <div className="trv-files">
                      <Attachments
                        objectType="travel_request"
                        objectId={r.id}
                        bucket="general"
                        label="Confirmations and itineraries"
                        roles={['attachment', 'evidence']}
                        accept=".pdf,.png,.jpg,.jpeg,.eml,.doc,.docx"
                        describe
                      />
                    </div>
                  )}

                  {!['completed', 'cancelled'].includes(r.status) && (
                    <div className="trv-cancel">
                      <button className="link-btn" disabled={busy}
                              onClick={() => {
                                const why = window.prompt('Why is the trip cancelled? Optional, '
                                                          + 'but the record keeps it.');
                                if (why === null) return;
                                call('cancel_travel_request',
                                     { p_request: r.id, p_note: why.trim() }, 'Cancelled.');
                              }}>
                        Cancel this trip
                      </button>
                    </div>
                  )}

                  {r.status === 'cancelled' && r.cancel_reason && (
                    <p className="muted">Cancelled: {r.cancel_reason}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* The perk card: tenant DATA, rendered only where a tenant configured one. Whatever
          arrangement a company has for its people's own holidays is that company's, and no
          part of it lives in shared code. */}
      {perk && (
        <section className="card trv-perk">
          <h2>{perk.title}</h2>
          <p>{perk.text}</p>
          <a className="btn" href={perk.url} target="_blank" rel="noreferrer">
            {perk.linkLabel ?? 'Find out more'}
          </a>
        </section>
      )}

      {editing && (
        <RequestDialog row={editing} live={live} me={me}
                       onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

/* =====================================================================================
   The booking channels an approved request offers, straight from tenant configuration.

   Three shapes. A PROVIDER channel (TravelPerk) deep-links into the provider's app with
   the summary on the clipboard, because its API has no booking-creation endpoint and the
   person booking picks the seat there. An EMAIL channel opens a prefilled itinerary
   request to the fulfilment inbox. A DIRECT channel has no intermediary: the traveller
   books it themselves, so recording the handoff and the booking is one act.
   ===================================================================================== */
function BookingChannels({ r, channels, busy, onHandOff, onBookedDirect, onCopy }: {
  r: Request; channels: TravelBookingChannel[]; busy: boolean;
  onHandOff: (r: Request, ch: TravelBookingChannel) => void;
  onBookedDirect: (r: Request, ch: TravelBookingChannel) => void;
  onCopy: (text: string) => void;
}) {
  const summary = tripSummary(r);
  if (channels.length === 0) {
    return (
      <div className="trv-booking">
        <p>
          Approved. No booking channels are configured for this tenant, so arrange the trip
          however this company does and mark it booked here, with the confirmation attached.
        </p>
        <div className="trv-actions">
          <button className="btn primary" disabled={busy} onClick={() => onBookedDirect(r, {
            key: 'unconfigured', label: 'Booked directly',
            instructions: '' })}>
            Mark as booked
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="trv-booking">
      <p><strong>Approved.</strong> Hand it to a booking channel:</p>
      {channels.map((ch) => (
        <div key={ch.key} className="trv-channel">
          <div className="trv-channel-main">
            <strong>{ch.label}</strong>
            <p>{ch.instructions}</p>
            {ch.provider === 'travelperk' && (
              <p className="trv-conn">{describeTravelPerkConnection(ch)}</p>
            )}
          </div>
          <div className="trv-actions">
            {ch.provider === 'travelperk' && (
              <button className="btn primary" disabled={busy}
                      onClick={() => {
                        onCopy(summary);
                        window.open(travelPerkAppUrl(ch), '_blank', 'noopener');
                        onHandOff(r, ch);
                      }}>
                Book in TravelPerk
              </button>
            )}
            {!ch.provider && ch.email && (
              <a className="btn primary"
                 href={`mailto:${ch.email}?subject=${encodeURIComponent(
                         `Travel booking request ${r.reference ?? ''}: ${r.origin} to ${r.destination}`)}`
                      + `&body=${encodeURIComponent(summary)}`}
                 onClick={() => onHandOff(r, ch)}>
                Email {ch.label}
              </a>
            )}
            {!ch.provider && !ch.email && (
              <button className="btn primary" disabled={busy}
                      onClick={() => onBookedDirect(r, ch)}>
                Booked it, record it
              </button>
            )}
            <button className="btn" onClick={() => onCopy(summary)}>Copy trip summary</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =====================================================================================
   The request screen, new or amend. Warns when the same screen is open in another tab,
   keyed per module: two tabs that cannot see each other's half-written request are how
   the same trip gets asked for twice.
   ===================================================================================== */
function RequestDialog({ row, live, me, onClose, onSaved }: {
  row: Partial<Request>; live: boolean; me: string | null;
  onClose: () => void; onSaved: () => void;
}) {
  const ref = useDialog(onClose);
  const elsewhere = useTabPresence('travel.new-request');
  const [f, setF] = useState({
    traveller: row.traveller ?? '',
    purpose: row.purpose ?? '',
    origin: row.origin ?? '',
    destination: row.destination ?? '',
    depart_on: row.depart_on ?? '',
    return_on: row.return_on ?? '',
    mode_preferences: row.mode_preferences ?? '',
    estimated_cost: row.estimated_cost != null ? String(row.estimated_cost) : '',
    currency: row.currency ?? BASE,
    cost_centre: row.cost_centre ?? '',
    notes: row.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const ready = f.traveller.trim() && f.purpose.trim() && f.origin.trim()
    && f.destination.trim() && f.depart_on;

  async function save() {
    setBusy(true); setErr(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const values = {
        traveller: f.traveller.trim(),
        purpose: f.purpose.trim(),
        origin: f.origin.trim(),
        destination: f.destination.trim(),
        depart_on: f.depart_on,
        return_on: f.return_on || null,
        mode_preferences: f.mode_preferences.trim() || null,
        estimated_cost: f.estimated_cost ? Number(f.estimated_cost) : null,
        currency: f.currency || null,
        cost_centre: f.cost_centre.trim() || null,
        notes: f.notes.trim() || null,
      };
      const led = supabase().schema('ledger');
      const { error } = row.id
        ? await led.from('travel_requests').update(values).eq('id', row.id)
        : await led.from('travel_requests').insert({ ...values, member_id: me });
      if (error) throw new Error(error.message);
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf trv-dialog" role="dialog" aria-modal="true" aria-label="Travel request"
           ref={ref} onClick={(e) => e.stopPropagation()}>
        <header className="cf-head">
          <h2>{row.id ? 'Amend the request' : 'New travel request'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cf-body">
          {elsewhere && (
            <p className="trv-tabwarn" role="alert">
              A travel request screen is already open in another tab or window. Two tabs
              cannot see each other's half-written request, which is how the same trip gets
              asked for twice. Use one tab.
            </p>
          )}

          <div className="cf-row">
            <label className="cf-field"><span>Who is travelling</span>
              <input value={f.traveller} onChange={set('traveller')}
                     placeholder="Usually you; sometimes a candidate or a guest" /></label>
            <label className="cf-field"><span>Purpose</span>
              <input value={f.purpose} onChange={set('purpose')}
                     placeholder="What the trip is for, in one line" /></label>
          </div>
          <div className="cf-row">
            <label className="cf-field"><span>From</span>
              <input value={f.origin} onChange={set('origin')} /></label>
            <label className="cf-field"><span>To</span>
              <input value={f.destination} onChange={set('destination')} /></label>
          </div>
          <div className="cf-row">
            <label className="cf-field"><span>Outbound</span>
              <input type="date" value={f.depart_on} onChange={set('depart_on')} /></label>
            <label className="cf-field"><span>Return (empty for one way)</span>
              <input type="date" value={f.return_on} onChange={set('return_on')} /></label>
          </div>
          <label className="cf-field"><span>Mode preferences</span>
            <input value={f.mode_preferences} onChange={set('mode_preferences')}
                   placeholder="Rail preferred; no overnight flights" /></label>
          <div className="cf-row">
            <label className="cf-field trv-ccy"><span>Currency</span>
              <select value={f.currency} onChange={set('currency')}>
                {[BASE, ...['GBP', 'EUR', 'USD', 'CHF', 'SEK', 'DKK', 'NOK', 'PLN', 'AUD',
                            'CAD', 'JPY', 'AED', 'SGD', 'ZAR'].filter((c) => c !== BASE)]
                  .map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="cf-field"><span>Estimated cost</span>
              <input type="number" step="0.01" min="0" value={f.estimated_cost}
                     onChange={set('estimated_cost')}
                     placeholder="Best estimate; the approver sees it" /></label>
            <label className="cf-field"><span>Cost centre</span>
              <input value={f.cost_centre} onChange={set('cost_centre')}
                     placeholder="If this company uses them" /></label>
          </div>
          <label className="cf-field"><span>Notes</span>
            <textarea rows={2} value={f.notes} onChange={set('notes')}
                      placeholder="Anything the approver or the booker needs to know" /></label>

          <p className="cf-note">
            Submitting sends it to your approver: your line manager from Crew, or the owner.
            The estimate is what they approve against; the actual costs are claimed through
            Expenses afterwards, with receipts.
          </p>

          {!live && (
            <p className="cf-note">
              This demonstration build does not store requests. On a live tenant this saves
              a draft you submit for approval.
            </p>
          )}
          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-save" onClick={save}
                    disabled={busy || !live || !ready}>
              {busy ? 'Saving' : row.id ? 'Save changes' : 'Save draft'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function Travel() {
  return <Boundary what="Travel"><TravelView /></Boundary>;
}
